// src/pages/Usuarios.js
//
// Gestión de usuarios conectada al backend real (/api/pos/usuarios-gestion).
//
// Visibilidad automática según el rol del usuario autenticado:
//   - SUPERADMIN     → puede filtrar por empresa (por defecto ve todas).
//   - ADMIN_NEGOCIO  → siempre ve solo su empresa (backend lo fuerza).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Typography, Stack, Chip, Avatar, Button,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  TextField, MenuItem, InputAdornment, LinearProgress, Alert, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import SearchIcon         from '@mui/icons-material/Search';
import RefreshIcon        from '@mui/icons-material/Refresh';
import PeopleIcon         from '@mui/icons-material/People';
import AddIcon            from '@mui/icons-material/Add';
import EditIcon           from '@mui/icons-material/Edit';
import BlockIcon          from '@mui/icons-material/Block';
import LockOpenIcon       from '@mui/icons-material/LockOpen';
import LockResetIcon      from '@mui/icons-material/LockReset';
import InfoOutlinedIcon   from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../context/AuthContext';
import { useApp }  from '../context/AppContext';
import { usuarios as apiUsuarios, empresas as apiEmpresas } from '../api/posApi';

const ROLES_COLOR = {
  SUPERADMIN:     'error',
  ADMIN_NEGOCIO:  'primary',
  VECI:           'success',
  DOMICILIARIO:   'warning',
  SIN_ROL:        'default',
};

const ROLES_LABEL = {
  SUPERADMIN:     'Superadmin',
  ADMIN_NEGOCIO:  'Admin negocio',
  VECI:           'Veci',
  DOMICILIARIO:   'Domiciliario',
  SIN_ROL:        'Sin rol',
};

const fmtFecha = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-CO'); } catch { return iso; }
};

const Usuarios = () => {
  const { user }   = useAuth();
  const { notify } = useApp();
  const esSuperadmin = user?.entidadTipo === 'SUPERADMIN';

  const [datos, setDatos] = useState({ items: [], total: 0, empresaIdAplicada: null });
  const [cargando, setCargando] = useState(false);
  const [tick, setTick] = useState(0);

  // Filtros
  const [empresaId, setEmpresaId] = useState('');  // solo para superadmin
  const [empresasList, setEmpresasList] = useState([]);
  const [search, setSearch] = useState('');
  const [rol, setRol] = useState('');
  const [estado, setEstado] = useState('');
  const [dialogNuevo, setDialogNuevo] = useState(false);
  const [dialogAccion, setDialogAccion] = useState(null);

  const bloquearUsuario = async (u) => {
    if (!window.confirm(`¿Bloquear al usuario ${u.nombre || u.email}?`)) return;
    try {
      await apiUsuarios.bloquear(u.id);
      notify('Usuario bloqueado.', 'success');
      setTick(t => t + 1);
    } catch (e) { notify(e.mensajeUsuario || 'No se pudo bloquear.', 'error'); }
  };

  const desbloquearUsuario = async (u) => {
    try {
      await apiUsuarios.desbloquear(u.id);
      notify('Usuario desbloqueado.', 'success');
      setTick(t => t + 1);
    } catch (e) { notify(e.mensajeUsuario || 'No se pudo desbloquear.', 'error'); }
  };

  // Cargar lista de empresas para el filtro (solo superadmin)
  useEffect(() => {
    if (!esSuperadmin) return;
    (async () => {
      try {
        const r = await apiEmpresas.listar();
        setEmpresasList(Array.isArray(r) ? r : []);
      } catch { /* silent */ }
    })();
  }, [esSuperadmin]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const filtros = {
        page: 1, size: 200,
        empresaId: esSuperadmin && empresaId ? Number(empresaId) : undefined,
        search: search.trim() || undefined,
        rol:    rol || undefined,
        estado: estado || undefined,
      };
      const r = await apiUsuarios.listar(filtros);
      setDatos({
        items: Array.isArray(r?.items) ? r.items : [],
        total: r?.total || 0,
        empresaIdAplicada: r?.empresaIdAplicada ?? null,
      });
      if (r?.warning) notify(r.warning, 'warning');
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo cargar la lista de usuarios.', 'error');
      setDatos({ items: [], total: 0, empresaIdAplicada: null });
    } finally { setCargando(false); }
  }, [esSuperadmin, empresaId, search, rol, estado, notify]);

  useEffect(() => { cargar(); }, [cargar, tick]);

  // Debounce de search
  useEffect(() => {
    const t = setTimeout(() => setTick(x => x + 1), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const empresaAplicadaNombre = useMemo(() => {
    if (!datos.empresaIdAplicada) return null;
    const e = empresasList.find(x => (x.empresaId || x.EmpresaId) === datos.empresaIdAplicada);
    return e?.razonSocial || e?.RazonSocial || `Empresa #${datos.empresaIdAplicada}`;
  }, [datos.empresaIdAplicada, empresasList]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <PeopleIcon color="primary" fontSize="large" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700}>Usuarios</Typography>
          <Typography variant="body2" color="text.secondary">
            {esSuperadmin
              ? 'Vista global de todos los usuarios de la plataforma.'
              : 'Usuarios de tu empresa.'}
          </Typography>
        </Box>
        <IconButton onClick={() => setTick(t => t + 1)}><RefreshIcon /></IconButton>
        <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => setDialogNuevo(true)}>
          Nuevo usuario
        </Button>
      </Stack>

      {/* Filtros */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            fullWidth size="small" placeholder="Buscar por nombre, correo o teléfono"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />

          {esSuperadmin && (
            <TextField select size="small" label="Empresa" sx={{ minWidth: 220 }}
                       value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}>
              <MenuItem value="">Todas las empresas</MenuItem>
              {empresasList.map(e => (
                <MenuItem key={e.empresaId || e.EmpresaId} value={e.empresaId || e.EmpresaId}>
                  {e.razonSocial || e.RazonSocial}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField select size="small" label="Rol" sx={{ minWidth: 170 }}
                     value={rol} onChange={(e) => setRol(e.target.value)}>
            <MenuItem value="">Todos los roles</MenuItem>
            <MenuItem value="SUPERADMIN">Superadmin</MenuItem>
            <MenuItem value="ADMIN_NEGOCIO">Admin negocio</MenuItem>
            <MenuItem value="VECI">Veci</MenuItem>
            <MenuItem value="DOMICILIARIO">Domiciliario</MenuItem>
          </TextField>

          <TextField select size="small" label="Estado" sx={{ minWidth: 170 }}
                     value={estado} onChange={(e) => setEstado(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="activo">Activo</MenuItem>
            <MenuItem value="sin_confirmar">Sin confirmar</MenuItem>
            <MenuItem value="bloqueado">Bloqueado</MenuItem>
          </TextField>
        </Stack>
      </Card>

      {/* Info del alcance */}
      {esSuperadmin ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Vista de <b>Superadmin</b>: {empresaAplicadaNombre
            ? <>filtrando por empresa <b>{empresaAplicadaNombre}</b>.</>
            : <>viendo usuarios de <b>todas las empresas</b>.</>}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Estás viendo solo los usuarios de tu empresa. Para gestionar la
          plataforma completa se requiere rol Superadmin.
        </Alert>
      )}

      {/* Tabla */}
      <Card>
        {cargando && <LinearProgress />}
        {!cargando && datos.items.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>Sin usuarios que coincidan con los filtros.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Correo</TableCell>
                  <TableCell>Teléfono</TableCell>
                  <TableCell>Rol</TableCell>
                  {esSuperadmin && <TableCell>Empresa</TableCell>}
                  <TableCell>Creado</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datos.items.map(u => (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ width: 34, height: 34,
                                      bgcolor: `${ROLES_COLOR[u.rolPrincipal] || 'primary'}.main`,
                                      fontSize: 13 }}>
                          {u.iniciales || (u.nombre || u.email || '?').slice(0, 2).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{u.nombre || '(sin nombre)'}</Typography>
                          {u.emailConfirmado ? null : (
                            <Typography variant="caption" color="warning.main">Sin confirmar</Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Typography variant="body2">{u.email}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{u.telefono || '—'}</Typography></TableCell>
                    <TableCell>
                      <Chip size="small"
                            color={ROLES_COLOR[u.rolPrincipal] || 'default'}
                            label={ROLES_LABEL[u.rolPrincipal] || u.rolPrincipal} />
                    </TableCell>
                    {esSuperadmin && (
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {u.tenantNombre || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell><Typography variant="caption">{fmtFecha(u.fechaCreacion)}</Typography></TableCell>
                    <TableCell align="center">
                      {u.bloqueado ? (
                        <Chip size="small" color="error"   label="Bloqueado" />
                      ) : u.activo ? (
                        <Chip size="small" color="success" label="Activo" />
                      ) : (
                        <Chip size="small" color="warning" label="Sin confirmar" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar datos">
                        <IconButton size="small" onClick={() => setDialogAccion({ tipo: 'editar', usuario: u })}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cambiar contraseña">
                        <IconButton size="small" onClick={() => setDialogAccion({ tipo: 'reset', usuario: u })}>
                          <LockResetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {u.bloqueado ? (
                        <Tooltip title="Desbloquear">
                          <IconButton size="small" color="success"
                                      onClick={() => desbloquearUsuario(u)}>
                            <LockOpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Bloquear">
                          <IconButton size="small" color="warning"
                                      onClick={() => bloquearUsuario(u)}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {datos.items.length > 0 && (
          <Box sx={{ p: 1.5, textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              Mostrando {datos.items.length} de {datos.total} usuarios
            </Typography>
          </Box>
        )}
      </Card>

      {dialogNuevo && (
        <DialogNuevoUsuario
          esSuperadmin={esSuperadmin}
          onClose={() => setDialogNuevo(false)}
          onCreado={() => { setDialogNuevo(false); setTick(t => t + 1); notify('Usuario creado.', 'success'); }}
          onError={(m) => notify(m, 'error')} />
      )}

      {dialogAccion?.tipo === 'editar' && (
        <DialogEditarUsuario usuario={dialogAccion.usuario}
          onClose={() => setDialogAccion(null)}
          onGuardado={() => { setDialogAccion(null); setTick(t => t + 1); notify('Datos actualizados.', 'success'); }}
          onError={(m) => notify(m, 'error')} />
      )}

      {dialogAccion?.tipo === 'reset' && (
        <DialogResetPassword usuario={dialogAccion.usuario}
          onClose={() => setDialogAccion(null)}
          onDone={() => { setDialogAccion(null); notify('Contraseña actualizada.', 'success'); }}
          onError={(m) => notify(m, 'error')} />
      )}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Dialog Nuevo Usuario
// ══════════════════════════════════════════════════════════════════════
//
// Comportamiento según el rol del usuario autenticado:
//
//   - SUPERADMIN: dialog completo con formulario. Consume el endpoint
//     heredado /api/holaveci/admin/usuarios. Los roles creables desde
//     aquí son SUPERADMIN e INVERSIONISTA (limitación del heredado —
//     los demás roles tienen sus flujos propios de registro).
//
//   - ADMIN_NEGOCIO: dialog explicativo con contexto claro:
//     * ADMIN_NEGOCIO se registra por el flujo de aprobación de negocios
//       en la administración de HolaVeci.
//     * Los empleados de tienda (cajero, vendedor, repartidor) son
//       perfiles de permisos sobre saas.UsuariosTenant que se habilitan
//       en el módulo C10 (aún no implementado).
//   No damos una acción que no funcione: le explicamos el mapa.

function DialogNuevoUsuario({ esSuperadmin, onClose, onCreado, onError }) {
  const [f, setF] = useState({
    nombre: '', apellido: '', correo: '', telefono: '',
    password: '', rol: esSuperadmin ? 'SUPERADMIN' : 'ADMIN_NEGOCIO',
    empresaId: '',
  });
  const [empresasList, setEmpresasList] = useState([]);
  const [cupo, setCupo] = useState(null);
  const [g, setG] = useState(false);

  // Superadmin: cargar lista de empresas para el selector
  useEffect(() => {
    if (!esSuperadmin) return;
    (async () => {
      try {
        const r = await apiEmpresas.listar();
        setEmpresasList(Array.isArray(r) ? r : []);
      } catch { /* silent */ }
    })();
  }, [esSuperadmin]);

  // Admin negocio: consultar cupo de admins disponibles
  useEffect(() => {
    if (esSuperadmin) return;
    (async () => {
      try {
        const c = await apiUsuarios.cupoAdmins();
        setCupo(c);
      } catch { /* silent */ }
    })();
  }, [esSuperadmin]);

  const submit = async () => {
    if (!f.nombre.trim() || !f.apellido.trim() || !f.correo.trim()
        || !f.telefono.trim() || !f.password.trim()) {
      onError('Todos los campos son obligatorios.'); return;
    }
    if (f.password.length < 6) {
      onError('La contraseña debe tener al menos 6 caracteres.'); return;
    }
    if (esSuperadmin && f.rol === 'ADMIN_NEGOCIO' && !f.empresaId) {
      onError('Selecciona una empresa para el nuevo admin.'); return;
    }

    // Normalizar teléfono a E.164 (+57...)
    let tel = f.telefono.replace(/\s|-/g, '');
    if (!tel.startsWith('+')) tel = tel.startsWith('57') ? `+${tel}` : `+57${tel}`;

    setG(true);
    try {
      await apiUsuarios.crear({
        nombre: f.nombre.trim(),
        apellido: f.apellido.trim(),
        correo: f.correo.trim().toLowerCase(),
        telefono: tel,
        password: f.password,
        rol: f.rol,
        empresaId: esSuperadmin && f.rol === 'ADMIN_NEGOCIO' ? Number(f.empresaId) : undefined,
      });
      onCreado();
    } catch (e) {
      onError(e.mensajeUsuario || 'No se pudo crear el usuario.');
    } finally { setG(false); }
  };

  const sinCupo = !esSuperadmin && cupo && cupo.disponibles <= 0;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {esSuperadmin ? 'Nuevo usuario' : 'Nuevo administrador del negocio'}
      </DialogTitle>
      <DialogContent dividers>
        {esSuperadmin ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Como Superadmin puedes crear usuarios de rol <b>Superadmin</b> o
            <b> Admin de negocio</b>. Los vecis y domiciliarios se registran
            por sus flujos propios.
          </Alert>
        ) : (
          <>
            {cupo && (
              <Alert severity={sinCupo ? 'error' : 'info'} sx={{ mb: 2 }}>
                Cupo de admins de tu empresa:
                {' '}<b>{cupo.actuales}</b> de <b>{cupo.max}</b> usados
                {' '}(disponibles: <b>{cupo.disponibles}</b>).
                {sinCupo && ' Ya llegaste al límite. Solicita al Superadmin aumentar el plan.'}
              </Alert>
            )}
            {!cupo && <LinearProgress sx={{ mb: 2 }} />}
          </>
        )}

        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField required fullWidth label="Nombre"
                       value={f.nombre} disabled={sinCupo}
                       onChange={(e) => setF(s => ({ ...s, nombre: e.target.value }))} />
            <TextField required fullWidth label="Apellido"
                       value={f.apellido} disabled={sinCupo}
                       onChange={(e) => setF(s => ({ ...s, apellido: e.target.value }))} />
          </Stack>
          <TextField required fullWidth type="email" label="Correo electrónico"
                     value={f.correo} disabled={sinCupo}
                     onChange={(e) => setF(s => ({ ...s, correo: e.target.value }))} />
          <TextField required fullWidth label="Teléfono"
                     placeholder="+573001234567"
                     helperText="Formato internacional (+57...)"
                     value={f.telefono} disabled={sinCupo}
                     onChange={(e) => setF(s => ({ ...s, telefono: e.target.value }))} />
          <TextField required fullWidth type="password" label="Contraseña"
                     helperText="Mínimo 6 caracteres"
                     value={f.password} disabled={sinCupo}
                     onChange={(e) => setF(s => ({ ...s, password: e.target.value }))} />

          {esSuperadmin && (
            <>
              <TextField required select fullWidth label="Rol"
                         value={f.rol}
                         onChange={(e) => setF(s => ({ ...s, rol: e.target.value }))}>
                <MenuItem value="SUPERADMIN">Superadmin</MenuItem>
                <MenuItem value="ADMIN_NEGOCIO">Admin de negocio</MenuItem>
              </TextField>

              {f.rol === 'ADMIN_NEGOCIO' && (
                <TextField required select fullWidth label="Empresa"
                           helperText="Empresa a la que pertenecerá el nuevo admin"
                           value={f.empresaId}
                           onChange={(e) => setF(s => ({ ...s, empresaId: e.target.value }))}>
                  {empresasList.map(e => (
                    <MenuItem key={e.empresaId || e.EmpresaId} value={e.empresaId || e.EmpresaId}>
                      {e.razonSocial || e.RazonSocial}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={g || sinCupo}>
          {g ? 'Creando…' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Usuarios;

// ══════════════════════════════════════════════════════════════════════
// Dialog editar datos básicos
// ══════════════════════════════════════════════════════════════════════

function DialogEditarUsuario({ usuario, onClose, onGuardado, onError }) {
  const [nombre, apellido] = (usuario.nombre || '').split(' ');
  const [f, setF] = useState({
    nombre: nombre || '',
    apellido: apellido || '',
    correo: usuario.email || '',
    telefono: usuario.telefono || '',
  });
  const [g, setG] = useState(false);

  const submit = async () => {
    setG(true);
    try {
      await apiUsuarios.editar(usuario.id, {
        nombre: f.nombre.trim() || undefined,
        apellido: f.apellido.trim() || undefined,
        correo: f.correo.trim() || undefined,
        telefono: f.telefono.trim() || undefined,
      });
      onGuardado();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo guardar.'); }
    finally { setG(false); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Editar {usuario.nombre || usuario.email}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField fullWidth label="Nombre" value={f.nombre}
                       onChange={(e) => setF(s => ({ ...s, nombre: e.target.value }))} />
            <TextField fullWidth label="Apellido" value={f.apellido}
                       onChange={(e) => setF(s => ({ ...s, apellido: e.target.value }))} />
          </Stack>
          <TextField fullWidth label="Correo" type="email" value={f.correo}
                     onChange={(e) => setF(s => ({ ...s, correo: e.target.value }))} />
          <TextField fullWidth label="Teléfono" placeholder="+573001234567"
                     value={f.telefono}
                     onChange={(e) => setF(s => ({ ...s, telefono: e.target.value }))} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={g}>
          {g ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Dialog reset password
// ══════════════════════════════════════════════════════════════════════

function DialogResetPassword({ usuario, onClose, onDone, onError }) {
  const [pass, setPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [g, setG] = useState(false);

  const submit = async () => {
    if (pass.length < 6) { onError('Mínimo 6 caracteres.'); return; }
    if (pass !== confirm) { onError('Las contraseñas no coinciden.'); return; }
    setG(true);
    try {
      await apiUsuarios.resetPassword(usuario.id, pass);
      onDone();
    } catch (e) { onError(e.mensajeUsuario || 'No se pudo actualizar.'); }
    finally { setG(false); }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Cambiar contraseña</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Estás reseteando la contraseña de <b>{usuario.nombre || usuario.email}</b>.
          Ellos deberán entrar con la nueva y cambiarla desde su perfil.
        </Alert>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField fullWidth type="password" label="Nueva contraseña"
                     value={pass} onChange={(e) => setPass(e.target.value)}
                     helperText="Mínimo 6 caracteres" />
          <TextField fullWidth type="password" label="Confirmar contraseña"
                     value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={g}>Cancelar</Button>
        <Button variant="contained" color="warning" onClick={submit} disabled={g}>
          {g ? 'Actualizando…' : 'Actualizar contraseña'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
